import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { initAuth, setupAuthListener, setLoading } from './features/auth/authSlice';
import { fetchCart } from './features/cart/cartSlice';
import VendorGuard from './components/vendor/VendorGuard';
import VendorPortalBoundary from './components/vendor/VendorPortalBoundary';
import AdminGuard from './components/admin/AdminGuard';
import AdminPortalBoundary from './components/admin/AdminPortalBoundary';
import LanguageSwitcher from './components/LanguageSwitcher';
import { isAdminUser, isApprovedVendorUser } from './utils/authRoles';

// Lazy load pages
const HomePage = React.lazy(() => import('./pages/HomePage'));
const ProductsPage = React.lazy(() => import('./pages/ProductsPage'));
const ProductDetailPage = React.lazy(() => import('./pages/ProductDetailPage'));
const EditorPage = React.lazy(() => import('./pages/EditorPage'));
const CartPage = React.lazy(() => import('./pages/CartPage'));
const CheckoutPage = React.lazy(() => import('./pages/CheckoutPage'));
const OrdersPage = React.lazy(() => import('./pages/OrdersPage'));
const OrderDetailPage = React.lazy(() => import('./pages/OrderDetailPage'));
const QuoteRequestFormPage = React.lazy(() => import('./pages/QuoteRequestFormPage'));
const QuotesPage = React.lazy(() => import('./pages/QuotesPage'));
const QuoteRequestDetailPage = React.lazy(() => import('./pages/QuoteRequestDetailPage'));
const DesignsPage = React.lazy(() => import('./pages/DesignsPage'));
const LoginPage = React.lazy(() => import('./pages/LoginPage'));
const SignupPage = React.lazy(() => import('./pages/SignupPage'));
const SupplierJoinPage = React.lazy(() => import('./pages/SupplierJoinPage'));
const ForgotPasswordPage = React.lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = React.lazy(() => import('./pages/ResetPasswordPage'));
const ProfilePage = React.lazy(() => import('./pages/ProfilePage'));
const AdminMfaPage = React.lazy(() => import('./pages/AdminMfaPage'));
const AuthCallbackPage = React.lazy(() => import('./pages/AuthCallbackPage'));
const EmailVerificationPage = React.lazy(() => import('./pages/EmailVerificationPage'));
const PaymentCallbackPage = React.lazy(() => import('./pages/PaymentCallbackPage'));
const PaymentSuccessPage = React.lazy(() => import('./pages/PaymentSuccessPage'));
const PaymentFailedPage = React.lazy(() => import('./pages/PaymentFailedPage'));
const LegalPage = React.lazy(() => import('./pages/LegalPage'));
const NotFoundPage = React.lazy(() => import('./pages/NotFoundPage'));
const DesignSystemPage = process.env.NODE_ENV !== 'production'
  ? React.lazy(() => import('./pages/DesignSystemPage'))
  : null;

// Vendor portal (Phase 1 foundation)
const VendorLayout = React.lazy(() => import('./components/vendor/VendorLayout'));
const VendorDashboardPage = React.lazy(() => import('./pages/vendor/VendorDashboardPage'));
const VendorOrdersPage = React.lazy(() => import('./pages/vendor/VendorOrdersPage'));
const VendorOrderDetailPage = React.lazy(() => import('./pages/vendor/VendorOrderDetailPage'));
const VendorQuoteRequestsPage = React.lazy(() => import('./pages/vendor/VendorQuoteRequestsPage'));
const VendorQuoteRequestDetailPage = React.lazy(() => import('./pages/vendor/VendorQuoteRequestDetailPage'));
const VendorFastDeliveryPage = React.lazy(() => import('./pages/vendor/VendorFastDeliveryPage'));
const VendorDielineUploadPage = React.lazy(() => import('./pages/vendor/VendorDielineUploadPage'));
const VendorPerformancePage = React.lazy(() => import('./pages/vendor/VendorPerformancePage'));
const VendorProfilePage = React.lazy(() => import('./pages/vendor/VendorProfilePage'));

// Admin panel (Phase 1 foundation)
const AdminLayout = React.lazy(() => import('./components/admin/AdminLayout'));
const AdminDashboardPage = React.lazy(() => import('./pages/admin/AdminDashboardPage'));
const AdminOrdersPage = React.lazy(() => import('./pages/admin/AdminOrdersPage'));
const AdminOrderDetailPage = React.lazy(() => import('./pages/admin/AdminOrderDetailPage'));
const AdminQuoteRequestsPage = React.lazy(() => import('./pages/admin/AdminQuoteRequestsPage'));
const AdminQuoteRequestDetailPage = React.lazy(() => import('./pages/admin/AdminQuoteRequestDetailPage'));
const AdminFastDeliveryPage = React.lazy(() => import('./pages/admin/AdminFastDeliveryPage'));
const AdminProductsPage = React.lazy(() => import('./pages/admin/AdminProductsPage'));
const AdminVendorsPage = React.lazy(() => import('./pages/admin/AdminVendorsPage'));
const AdminAnalyticsPage = React.lazy(() => import('./pages/admin/AdminAnalyticsPage'));

// Loading component
const LoadingScreen = () => {
  const { t } = useTranslation();

  return (
    <div className="loading-screen" role="status" aria-live="polite">
      <div className="logo text-center">
        <h1 className="text-4xl font-bold text-primary-600">PackDealz</h1>
        <div className="spinner mt-4" aria-hidden="true"></div>
        <span className="sr-only">{t('common.loading')}</span>
      </div>
    </div>
  );
};

// Protected Route component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useSelector((state) => state.auth);
  const location = useLocation();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  return children;
};

function App() {
  const dispatch = useDispatch();
  const { isAuthenticated, loading, user, profile } = useSelector((state) => state.auth);
  const { i18n } = useTranslation();
  const location = useLocation();
  const approvedVendor = isApprovedVendorUser(user, profile);
  const adminUser = isAdminUser(user, profile);
  const authUtilityPaths = new Set([
    '/login', '/signup', '/forgot-password', '/reset-password', '/verify-email', '/auth/callback',
  ]);

  useEffect(() => {
    // Skip auth init on the callback page — it handles auth itself
    // and calling getSession() simultaneously causes AbortError
    const isCallbackPage = window.location.pathname === '/auth/callback';
    if (!isCallbackPage) {
      dispatch(initAuth());
    } else {
      dispatch(setLoading(false));
    }

    // Set up auth state listener
    const { data: { subscription } } = setupAuthListener(dispatch);

    return () => {
      subscription?.unsubscribe();
    };
  }, [dispatch]);

  useEffect(() => {
    // Buyer carts are not part of the approved vendor workspace.
    if (isAuthenticated && !approvedVendor && !adminUser) {
      dispatch(fetchCart());
    }
  }, [adminUser, approvedVendor, dispatch, isAuthenticated]);

  useEffect(() => {
    // Set initial direction based on language
    document.documentElement.setAttribute('dir', i18n.dir(i18n.resolvedLanguage || i18n.language));
  }, [i18n, i18n.language, i18n.resolvedLanguage]);

  // Show loading screen while initializing auth
  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="App">
      {authUtilityPaths.has(location.pathname) && (
        <div className="fixed end-4 top-4 z-50 rounded-md border border-[#dce4df] bg-white/95 shadow-sm backdrop-blur">
          <LanguageSwitcher />
        </div>
      )}
      <AdminPortalBoundary fallback={<LoadingScreen />}>
        <VendorPortalBoundary fallback={<LoadingScreen />}>
          <React.Suspense fallback={<LoadingScreen />}>
          <Routes>
          {/* Public routes */}
          <Route path="/" element={<HomePage />} />
          <Route path="/products" element={<ProductsPage />} />
          <Route path="/products/:id" element={<ProductDetailPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/suppliers/join" element={<SupplierJoinPage />} />
          <Route path="/terms" element={<LegalPage type="terms" />} />
          <Route path="/privacy" element={<LegalPage type="privacy" />} />
          {DesignSystemPage && <Route path="/design-system" element={<DesignSystemPage />} />}
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/verify-email" element={<EmailVerificationPage />} />
          <Route path="/auth/callback" element={<AuthCallbackPage />} />
          
          {/* Payment callback routes (public - no auth required) */}
          <Route path="/payment/success" element={<PaymentSuccessPage />} />
          <Route path="/payment/failed" element={<PaymentFailedPage />} />
          <Route path="/payment/cancelled" element={<PaymentFailedPage />} />

          {/* Protected routes */}
          <Route
            path="/editor"
            element={
              <ProtectedRoute>
                <EditorPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/editor/:productId"
            element={
              <ProtectedRoute>
                <EditorPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/editor/:productId/:designId"
            element={
              <ProtectedRoute>
                <EditorPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/cart"
            element={
              <ProtectedRoute>
                <CartPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/checkout"
            element={
              <ProtectedRoute>
                <CheckoutPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/quote/new"
            element={<QuoteRequestFormPage />}
          />
          <Route
            path="/quote/new/:productId"
            element={<QuoteRequestFormPage />}
          />
          <Route
            path="/quotes"
            element={
              <ProtectedRoute>
                <QuotesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/quotes/:id"
            element={
              <ProtectedRoute>
                <QuoteRequestDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/orders"
            element={
              <ProtectedRoute>
                <OrdersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/orders/:id"
            element={
              <ProtectedRoute>
                <OrderDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/orders/:id/payment-callback"
            element={
              <ProtectedRoute>
                <PaymentCallbackPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin-mfa"
            element={
              <ProtectedRoute>
                <AdminMfaPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/designs"
            element={
              <ProtectedRoute>
                <DesignsPage />
              </ProtectedRoute>
            }
          />

          {/* Vendor portal routes */}
          <Route
            path="/vendor"
            element={
              <VendorGuard fallback={<LoadingScreen />}>
                <VendorLayout />
              </VendorGuard>
            }
          >
            <Route index element={<Navigate to="/vendor/dashboard" replace />} />
            <Route path="dashboard" element={<VendorDashboardPage />} />
            <Route path="orders" element={<VendorOrdersPage />} />
            <Route path="orders/:orderId" element={<VendorOrderDetailPage />} />
            <Route path="quote-requests" element={<VendorQuoteRequestsPage />} />
            <Route path="quote-requests/:quoteRequestId" element={<VendorQuoteRequestDetailPage />} />
            <Route path="dielines/new" element={<VendorDielineUploadPage />} />
            <Route path="fast-delivery" element={<VendorFastDeliveryPage />} />
            <Route path="performance" element={<VendorPerformancePage />} />
            <Route path="profile" element={<VendorProfilePage />} />
          </Route>

          {/* Admin panel routes */}
          <Route
            path="/admin-panel"
            element={
              <AdminGuard fallback={<LoadingScreen />}>
                <AdminLayout />
              </AdminGuard>
            }
          >
            <Route index element={<AdminDashboardPage />} />
            <Route path="orders" element={<AdminOrdersPage />} />
            <Route path="orders/:orderId" element={<AdminOrderDetailPage />} />
            <Route path="quote-requests" element={<AdminQuoteRequestsPage />} />
            <Route path="quote-requests/:quoteRequestId" element={<AdminQuoteRequestDetailPage />} />
            <Route path="fast-delivery" element={<AdminFastDeliveryPage />} />
            <Route path="products" element={<AdminProductsPage />} />
            <Route path="vendors" element={<AdminVendorsPage />} />
            <Route path="analytics" element={<AdminAnalyticsPage />} />
          </Route>

          {/* 404 */}
          <Route path="*" element={<NotFoundPage />} />
          </Routes>
          </React.Suspense>
        </VendorPortalBoundary>
      </AdminPortalBoundary>
    </div>
  );
}

export default App;
